import { track } from "@amplitude/analytics-react-native";
import { useQuery } from "@apollo/client";
import { useCallback } from "react";
import { CALLER_UPLOADS_SECTION } from "src/api/featureFlags";
import GetGatheringForUploadAgreementDocument from "src/api/graphql/__generated__/documents/GetGatheringForUploadAgreementDocument";
import { useGatheringStore } from "src/contexts/GatheringContext";
import openURL from "src/helpers/openURL";
import { alert } from "src/swsh-native";

const useUploadAgreement = () => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const { data, refetch } = useQuery(GetGatheringForUploadAgreementDocument, {
		variables: {
			input: {
				gatheringId,
			},
		},
		fetchPolicy: "cache-first",
	});
	const setViewMode = useGatheringStore((state) => state.setViewMode);
	const checkUploadAgreement = useCallback(async (): Promise<{
		shouldContinue: boolean;
		onUpload?: () => void;
	}> => {
		const gathering = data?.getGathering ?? (await refetch()).data.getGathering;
		const uploadAgreement = gathering?.uploadAgreement;
		if (!uploadAgreement) {
			return {
				shouldContinue: true,
			};
		}
		const { shouldContinue } = await new Promise<{
			shouldContinue: boolean;
		}>((resolve) => {
			const buttons: Parameters<typeof alert>[0]["buttons"] = [];
			const handleDismiss = () => {
				track("upload_agreement_cancel");
				resolve({ shouldContinue: false });
			};
			buttons.push({
				text: uploadAgreement.acceptText ?? "Got it",
				style: "default",
				isPreferred: true,
				onPress: () => {
					track("upload_agreement_agree");
					resolve({ shouldContinue: true });
				},
			});
			buttons.push({
				text: "Cancel",
				style: "cancel",
				onPress: handleDismiss,
			});
			const readMoreUrl = uploadAgreement.readMoreUrl;
			if (readMoreUrl) {
				buttons.push({
					text: "Read More",
					onPress: () => {
						track("upload_agreement_read_more");
						openURL(readMoreUrl, {
							target: "_blank",
						});
						resolve({ shouldContinue: false });
					},
				});
			}
			alert({
				title: uploadAgreement.title,
				message: uploadAgreement.content,
				buttons,
				onDismiss: handleDismiss,
				webTestID: "UploadAgreement",
			});
		});
		const onUpload =
			shouldContinue &&
			data?.getGathering?.uploadAgreement?.redirectToCallerPhotos &&
			CALLER_UPLOADS_SECTION
				? () => {
						setViewMode("AllCaller");
					}
				: undefined;
		return { shouldContinue, onUpload };
	}, [data?.getGathering, refetch, setViewMode]);
	return { checkUploadAgreement };
};
export default useUploadAgreement;
